import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import { Car, Pencil, Plus, Trash2 } from "lucide-react";
import { z } from "zod";
import {
  INVENTORY_STATUS_LABELS,
  VehicleConditionSchema,
  VehicleSourceSchema,
  VehicleStatusSchema,
  currencyForCountry,
  type InventoryVehicle,
  type UpdateInventoryVehicleDto,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  InventorySearchToolbar,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  buildInventorySearchRequest,
  cn,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";

/**
 * Admin Inventory page — full CRUD over a dealership's vehicles.
 *
 * Filter / search / sort UI is shared with the dealer (`@lumika/ui`'s
 * `InventorySearchToolbar`) and URL-driven. This page only owns the
 * vehicle grid + add/edit/delete dialogs; everything to do with
 * facet selection and sticky toolbar layout lives in the shared
 * component.
 */

type VehicleStatus = z.infer<typeof VehicleStatusSchema>;
type VehicleCondition = z.infer<typeof VehicleConditionSchema>;
type VehicleSource = z.infer<typeof VehicleSourceSchema>;

const STATUS_VARIANT: Record<VehicleStatus, "default" | "success" | "warning" | "secondary"> = {
  available: "success",
  intransit: "warning",
  pending: "warning",
  sold: "secondary",
};

const CONDITION_LABELS: Record<VehicleCondition, string> = {
  new: "New",
  used: "Used",
  certified: "Certified",
};

const SOURCE_LABELS: Record<VehicleSource, string> = {
  feed: "Feed",
  manual: "Manual",
  api: "API",
};

// Canonical entry vocabularies for spec fields stored as free strings.
// No shared export exists yet — keep these page-local until
// packages/shared grows a canonical enum. Feed values can be verbose
// ("8-Speed Automatic", "Sport Utility"); VocabSelect keeps the stored
// value selectable so editing never blanks or rewrites it.
const DRIVETRAIN_OPTIONS: readonly string[] = ["AWD", "4WD", "FWD", "RWD"];
const FUEL_OPTIONS: readonly string[] = [
  "Gasoline",
  "Diesel",
  "Hybrid",
  "Plug-in Hybrid",
  "Electric",
  "Flex Fuel",
];
const TRANSMISSION_OPTIONS: readonly string[] = ["Automatic", "Manual", "CVT", "Dual-clutch"];
const BODY_OPTIONS: readonly string[] = [
  "SUV",
  "Sedan",
  "Coupe",
  "Truck",
  "Van",
  "Minivan",
  "Hatchback",
  "Wagon",
  "Convertible",
];

/** Sentinel for optional free-string selects — maps to "" (unset). */
const NOT_SET = "none";

const VinPattern = /^[A-HJ-NPR-Z0-9]{11,17}$/i;

const optionalNumber = (max?: number) =>
  z
    .string()
    .optional()
    .refine(
      (v) => {
        if (!v || !v.trim()) return true;
        const num = Number(v);
        if (!Number.isFinite(num)) return false;
        if (max !== undefined && num > max) return false;
        return num >= 0;
      },
      { message: "Must be a positive number." },
    );

const VehicleFormSchema = z.object({
  vin: z.string().trim().toUpperCase().regex(VinPattern, "VIN must be 11–17 alphanumeric chars."),
  status: VehicleStatusSchema,
  condition: VehicleConditionSchema.or(z.literal("none")),
  rooftopId: z.string().uuid("Pick a rooftop."),
  source: VehicleSourceSchema.or(z.literal("none")),
  stock: z.string().trim().max(40).optional(),
  year: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{4}$/u.test(v), { message: "4-digit year." }),
  make: z.string().trim().max(40).optional(),
  model: z.string().trim().max(80).optional(),
  trim: z.string().trim().max(80).optional(),
  body: z.string().trim().max(40).optional(),
  color: z.string().trim().max(40).optional(),
  mileage: optionalNumber(2_000_000),
  driveTrain: z.string().trim().max(40).optional(),
  transmission: z.string().trim().max(40).optional(),
  engine: z.string().trim().max(60).optional(),
  fuel: z.string().trim().max(30).optional(),
  doors: optionalNumber(20),
  seats: optionalNumber(20),
  // Comma-separated in the form; split into string[] on submit.
  features: z.string().trim().max(1000).optional(),
  msrp: optionalNumber(2_000_000),
  price: optionalNumber(2_000_000),
  listPrice: optionalNumber(2_000_000),
  offeredPrice: optionalNumber(2_000_000),
  vdpUrl: z.string().url("Invalid URL.").or(z.literal("")).optional(),
});

type VehicleFormValues = z.infer<typeof VehicleFormSchema>;

const EMPTY: VehicleFormValues = {
  vin: "",
  status: "available",
  condition: "none",
  rooftopId: "",
  source: "manual",
  stock: "",
  year: "",
  make: "",
  model: "",
  trim: "",
  body: "",
  color: "",
  mileage: "",
  driveTrain: "",
  transmission: "",
  engine: "",
  fuel: "",
  doors: "",
  seats: "",
  features: "",
  msrp: "",
  price: "",
  listPrice: "",
  offeredPrice: "",
  vdpUrl: "",
};

function toEditValues(vehicle: InventoryVehicle): VehicleFormValues {
  return {
    vin: vehicle.vin,
    status: vehicle.status,
    condition: vehicle.condition ?? "none",
    rooftopId: vehicle.rooftopId,
    source: vehicle.source ?? "none",
    stock: vehicle.stock ?? "",
    year: vehicle.year !== null ? String(vehicle.year) : "",
    make: vehicle.make ?? "",
    model: vehicle.model ?? "",
    trim: vehicle.trim ?? "",
    body: vehicle.body ?? "",
    color: vehicle.color ?? "",
    mileage: vehicle.mileage !== null ? String(vehicle.mileage) : "",
    driveTrain: vehicle.driveTrain ?? "",
    transmission: vehicle.transmission ?? "",
    engine: vehicle.engine ?? "",
    fuel: vehicle.fuel ?? "",
    doors: vehicle.doors !== null ? String(vehicle.doors) : "",
    seats: vehicle.seats !== null ? String(vehicle.seats) : "",
    features: (vehicle.features ?? []).join(", "),
    msrp: vehicle.msrp !== null ? String(vehicle.msrp) : "",
    price: vehicle.price !== null ? String(vehicle.price) : "",
    listPrice: vehicle.listPrice !== null ? String(vehicle.listPrice) : "",
    offeredPrice: vehicle.offeredPrice !== null ? String(vehicle.offeredPrice) : "",
    vdpUrl: vehicle.vdpUrl ?? "",
  };
}

function maybe(value: string | undefined): string | null {
  return value && value.trim() ? value.trim() : null;
}

function numberOrNull(value: string | undefined): number | null {
  if (!value || !value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function intOrNull(value: string | undefined): number | null {
  const num = numberOrNull(value);
  if (num === null) return null;
  return Math.round(num);
}

/** Comma/newline-separated feature list → trimmed string[] (deduped). */
function parseFeatures(value: string | undefined): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(/[,\n]/u)) {
    const f = raw.trim();
    if (f) seen.add(f);
  }
  return [...seen];
}

function toPayload(values: VehicleFormValues): UpdateInventoryVehicleDto {
  return {
    status: values.status,
    condition: values.condition === "none" ? null : values.condition,
    rooftopId: values.rooftopId,
    source: values.source === "none" ? null : values.source,
    stock: maybe(values.stock),
    year: intOrNull(values.year),
    make: maybe(values.make) ?? undefined,
    model: maybe(values.model) ?? undefined,
    trim: maybe(values.trim) ?? undefined,
    body: maybe(values.body) ?? undefined,
    color: maybe(values.color) ?? undefined,
    mileage: intOrNull(values.mileage),
    driveTrain: maybe(values.driveTrain) ?? undefined,
    transmission: maybe(values.transmission) ?? undefined,
    engine: maybe(values.engine) ?? undefined,
    fuel: maybe(values.fuel) ?? undefined,
    doors: intOrNull(values.doors),
    seats: intOrNull(values.seats),
    features: parseFeatures(values.features),
    msrp: numberOrNull(values.msrp),
    price: numberOrNull(values.price),
    listPrice: numberOrNull(values.listPrice),
    offeredPrice: numberOrNull(values.offeredPrice),
    vdpUrl: maybe(values.vdpUrl) ?? undefined,
  };
}

function vehicleLabel(vehicle: InventoryVehicle): string {
  return (
    [vehicle.year, vehicle.make, vehicle.model, vehicle.trim].filter(Boolean).join(" ") ||
    vehicle.vin
  );
}

function formatPrice(value: number | null, currency: string): string {
  if (value === null) return "—";
  return value.toLocaleString(undefined, { style: "currency", currency, maximumFractionDigits: 0 });
}

const PAGE_SIZE = 24;

export function DealershipInventoryPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<InventoryVehicle | "new" | null>(null);
  const [pendingDelete, setPendingDelete] = useState<InventoryVehicle | null>(null);

  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const request = useMemo(
    () =>
      buildInventorySearchRequest(params, PAGE_SIZE, {
        // Admin URLs deep-link from the import detail view with an
        // `importId` filter; the shared toolbar honors it as a chip.
        includeImportId: true,
      }),
    [params],
  );

  const searchResult = useQuery({
    queryKey: ["admin-bdc", dealershipId, "inventory-search", request],
    queryFn: () => adminApi.searchInventory(dealershipId!, request),
    enabled: Boolean(dealershipId),
    placeholderData: (prev) => prev,
  });

  const rooftops = detailQuery.data?.rooftops ?? [];
  const currency = currencyForCountry(detailQuery.data?.country);

  const deleteMutation = useMutation({
    mutationFn: (vehicleId: string) => adminApi.removeInventory(dealershipId!, vehicleId),
    onSuccess: async () => {
      setPendingDelete(null);
      await invalidate(qc, dealershipId);
      toast.success("Vehicle removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function goToPage(page: number) {
    const next = new URLSearchParams(params);
    if (page <= 1) next.delete("page");
    else next.set("page", String(page));
    setParams(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const data = searchResult.data;
  const facets = data?.facets ?? null;
  const total = data?.total ?? 0;
  const page = Math.max(1, Number(params.get("page")) || 1);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Inventory"
        title="Vehicles"
        description={
          total > 0
            ? `${total.toLocaleString()} vehicles — feed-driven; manual create/edit/delete available to admins only.`
            : "VIN-keyed inventory used to ground AI leads and feed CRM workflows."
        }
        actions={
          <Button onClick={() => setEditing("new")}>
            <Plus className="h-4 w-4" />
            Add vehicle
          </Button>
        }
      />

      <InventorySearchToolbar
        params={params}
        onParamsChange={(next, opts) => setParams(next, opts)}
        facets={facets}
        loading={searchResult.isLoading && !facets}
        includeImportFilter
        currency={currency}
      />

      <section>
        {searchResult.error ? (
          <PageError
            title="Couldn't load inventory"
            description={(searchResult.error as Error).message}
          />
        ) : searchResult.isLoading && !data ? (
          <PageLoading message="Loading inventory…" />
        ) : !data || data.vehicles.length === 0 ? (
          <Section>
            {total > 0 ? (
              // Only reachable with an out-of-range page param (stale
              // bookmark / shrunken inventory) — the toolbar resets the
              // page on every filter edit.
              <EmptyState
                icon={<Car className="h-5 w-5" />}
                title="This page is past the end of the results"
                description="Inventory has changed since this link was created."
                action={
                  <Button variant="outline" size="sm" onClick={() => goToPage(1)}>
                    Back to first page
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Car className="h-5 w-5" />}
                title="No vehicles yet"
                description="Add vehicles manually or connect an inventory feed."
                action={
                  <Button onClick={() => setEditing("new")}>
                    <Plus className="h-4 w-4" />
                    Add vehicle
                  </Button>
                }
              />
            )}
          </Section>
        ) : (
          <>
            <div
              className={cn(
                "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
                searchResult.isPlaceholderData && "opacity-60",
              )}
            >
              {data.vehicles.map((vehicle) => (
                <VehicleAdminCard
                  key={vehicle.id}
                  vehicle={vehicle}
                  rooftopLabel={
                    facets?.rooftop.find((r) => r.value === vehicle.rooftopId)?.label ?? null
                  }
                  currency={currency}
                  onEdit={() => setEditing(vehicle)}
                  onDelete={() => setPendingDelete(vehicle)}
                />
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} — {data.vehicles.length} of {total.toLocaleString()}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1 || searchResult.isPlaceholderData}
                  onClick={() => goToPage(page - 1)}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages || searchResult.isPlaceholderData}
                  onClick={() => goToPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {editing !== null && dealershipId && (
        <VehicleFormDialog
          mode={editing === "new" ? "create" : "edit"}
          vehicle={editing === "new" ? null : editing}
          rooftops={rooftops}
          dealershipId={dealershipId}
          currency={currency}
          onClose={() => setEditing(null)}
        />
      )}

      <Dialog open={Boolean(pendingDelete)} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove vehicle</DialogTitle>
            <DialogDescription>
              {pendingDelete &&
                `${vehicleLabel(pendingDelete)} (VIN ${pendingDelete.vin}) will be removed from inventory.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => pendingDelete && deleteMutation.mutate(pendingDelete.id)}
            >
              {deleteMutation.isPending ? "Removing…" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vehicle card (admin)
// ---------------------------------------------------------------------------

function VehicleAdminCard({
  vehicle: v,
  rooftopLabel,
  currency,
  onEdit,
  onDelete,
}: {
  vehicle: InventoryVehicle;
  rooftopLabel: string | null;
  currency: string;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const title = vehicleLabel(v);
  const isSold = v.status === "sold";
  const price = v.offeredPrice ?? v.price;
  return (
    <div
      className={cn(
        "group flex flex-col overflow-hidden rounded-md border bg-card text-card-foreground transition hover:shadow-md",
        isSold && "opacity-75",
      )}
    >
      <div className="aspect-[4/3] overflow-hidden bg-muted">
        {v.photos && v.photos[0] ? (
          <img
            src={v.photos[0]}
            alt={title}
            className="size-full object-cover transition group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
            No photo
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <h4 className="line-clamp-2 text-sm font-semibold">{title}</h4>
          <Badge variant={STATUS_VARIANT[v.status]} className="shrink-0">
            {INVENTORY_STATUS_LABELS[v.status]}
          </Badge>
        </div>
        <p className="font-mono text-xs text-muted-foreground">
          VIN {v.vin} {v.stock ? `· Stock ${v.stock}` : ""}
        </p>
        <div className="flex flex-wrap items-end justify-between gap-2">
          <span className="text-base font-semibold">{formatPrice(price, currency)}</span>
          <span className="text-xs text-muted-foreground">
            {v.mileage !== null ? `${v.mileage.toLocaleString()} mi` : ""}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          {rooftopLabel ? (
            <Badge variant="secondary" className="text-[10px]">
              {rooftopLabel}
            </Badge>
          ) : (
            <span />
          )}
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Edit vehicle"
              onClick={onEdit}
              className="h-8 w-8"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Delete vehicle"
              onClick={onDelete}
              className="h-8 w-8"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
        {v.importId && (
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Feed-managed · last sync{" "}
            {v.lastSyncedAt
              ? new Date(v.lastSyncedAt).toLocaleDateString()
              : "—"}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vehicle form dialog
// ---------------------------------------------------------------------------

function VehicleFormDialog({
  mode,
  vehicle,
  rooftops,
  dealershipId,
  currency,
  onClose,
}: {
  mode: "create" | "edit";
  vehicle: InventoryVehicle | null;
  rooftops: { id: string; name: string }[];
  dealershipId: string;
  currency: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useZodForm(VehicleFormSchema, vehicle ? toEditValues(vehicle) : EMPTY);

  const createMutation = useMutation({
    mutationFn: (input: VehicleFormValues) =>
      adminApi.createInventory(dealershipId, {
        ...toPayload(input),
        // vin / rooftopId / status are optional on the shared Update
        // type but required for create — restate them as definite.
        vin: input.vin.trim().toUpperCase(),
        rooftopId: input.rooftopId,
        status: input.status,
        photos: [],
      }),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Vehicle added");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (input: VehicleFormValues) =>
      adminApi.updateInventory(dealershipId, vehicle!.id, toPayload(input)),
    onSuccess: async () => {
      await invalidate(qc, dealershipId);
      toast.success("Vehicle updated");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    if (mode === "create") createMutation.mutate(result.data);
    else updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const isPending = createMutation.isPending || updateMutation.isPending;
  // Block backdrop-click / Escape dismissal once the form differs from
  // its initial values — closing unmounts the dialog and loses the work.
  const initialValues = useMemo(() => (vehicle ? toEditValues(vehicle) : EMPTY), [vehicle]);
  const isDirty = Object.keys(initialValues).some(
    (k) => values[k as keyof VehicleFormValues] !== initialValues[k as keyof VehicleFormValues],
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Add vehicle" : "Edit vehicle"}</DialogTitle>
          <DialogDescription>
            Identity, mechanical specs, pricing, and operations metadata.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-5 overflow-y-auto -mx-1 px-1">
          <FieldGroup title="Identity">
            <FormField label="VIN" required error={errors.vin} className="sm:col-span-2">
              <Input
                value={values.vin}
                onChange={(e) => setField("vin", e.target.value)}
                autoFocus={mode === "create"}
                disabled={mode === "edit"}
                className="font-mono uppercase"
                placeholder="17-character VIN"
              />
            </FormField>
            <FormField label="Stock #" optional error={errors.stock}>
              <Input
                value={values.stock ?? ""}
                onChange={(e) => setField("stock", e.target.value)}
              />
            </FormField>
            <FormField label="Status" required error={errors.status}>
              <Select
                value={values.status}
                onValueChange={(v) => setField("status", v as VehicleStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VehicleStatusSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {INVENTORY_STATUS_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Condition" error={errors.condition}>
              <Select
                value={values.condition}
                onValueChange={(v) => setField("condition", v as VehicleCondition | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {VehicleConditionSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {CONDITION_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Source" error={errors.source}>
              <Select
                value={values.source}
                onValueChange={(v) => setField("source", v as VehicleSource | "none")}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {VehicleSourceSchema.options.map((option) => (
                    <SelectItem key={option} value={option}>
                      {SOURCE_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Rooftop" required error={errors.rooftopId} className="sm:col-span-2">
              <Select
                value={values.rooftopId}
                onValueChange={(v) => setField("rooftopId", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a rooftop" />
                </SelectTrigger>
                <SelectContent>
                  {rooftops.map((rooftop) => (
                    <SelectItem key={rooftop.id} value={rooftop.id}>
                      {rooftop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </FieldGroup>

          <FieldGroup title="Vehicle">
            <FormField label="Year" optional error={errors.year}>
              <Input
                value={values.year ?? ""}
                onChange={(e) => setField("year", e.target.value)}
                inputMode="numeric"
                maxLength={4}
              />
            </FormField>
            <FormField label="Make" optional error={errors.make}>
              <Input
                value={values.make ?? ""}
                onChange={(e) => setField("make", e.target.value)}
                placeholder="BMW"
              />
            </FormField>
            <FormField label="Model" optional error={errors.model}>
              <Input
                value={values.model ?? ""}
                onChange={(e) => setField("model", e.target.value)}
                placeholder="X5"
              />
            </FormField>
            <FormField label="Trim" optional error={errors.trim}>
              <Input
                value={values.trim ?? ""}
                onChange={(e) => setField("trim", e.target.value)}
                placeholder="xDrive40i"
              />
            </FormField>
            <FormField label="Body" optional error={errors.body}>
              <VocabSelect
                value={values.body ?? ""}
                options={BODY_OPTIONS}
                onChange={(v) => setField("body", v)}
              />
            </FormField>
            <FormField label="Mileage" optional error={errors.mileage}>
              <Input
                value={values.mileage ?? ""}
                onChange={(e) => setField("mileage", e.target.value)}
                inputMode="numeric"
              />
            </FormField>
            <FormField label="Color" optional error={errors.color}>
              <Input
                value={values.color ?? ""}
                onChange={(e) => setField("color", e.target.value)}
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Specs & powertrain">
            <FormField label="Drivetrain" optional error={errors.driveTrain}>
              <VocabSelect
                value={values.driveTrain ?? ""}
                options={DRIVETRAIN_OPTIONS}
                onChange={(v) => setField("driveTrain", v)}
              />
            </FormField>
            <FormField label="Transmission" optional error={errors.transmission}>
              <VocabSelect
                value={values.transmission ?? ""}
                options={TRANSMISSION_OPTIONS}
                onChange={(v) => setField("transmission", v)}
              />
            </FormField>
            <FormField label="Engine" optional error={errors.engine}>
              <Input
                value={values.engine ?? ""}
                onChange={(e) => setField("engine", e.target.value)}
                placeholder="2.0L Turbo I4"
              />
            </FormField>
            <FormField label="Fuel" optional error={errors.fuel}>
              <VocabSelect
                value={values.fuel ?? ""}
                options={FUEL_OPTIONS}
                onChange={(v) => setField("fuel", v)}
              />
            </FormField>
            <FormField label="Doors" optional error={errors.doors}>
              <Input
                value={values.doors ?? ""}
                onChange={(e) => setField("doors", e.target.value)}
                inputMode="numeric"
              />
            </FormField>
            <FormField label="Seats" optional error={errors.seats}>
              <Input
                value={values.seats ?? ""}
                onChange={(e) => setField("seats", e.target.value)}
                inputMode="numeric"
              />
            </FormField>
            <FormField
              className="sm:col-span-2"
              label="Features"
              optional
              error={errors.features}
              description="Comma-separated — e.g. Heated Seats, Apple CarPlay, Adaptive Cruise"
            >
              <Input
                value={values.features ?? ""}
                onChange={(e) => setField("features", e.target.value)}
                placeholder="Heated Seats, Sunroof, Backup Camera"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title={`Pricing (${currency})`}>
            <FormField label="MSRP" optional error={errors.msrp}>
              <Input
                value={values.msrp ?? ""}
                onChange={(e) => setField("msrp", e.target.value)}
                inputMode="decimal"
              />
            </FormField>
            <FormField label="Price" optional error={errors.price}>
              <Input
                value={values.price ?? ""}
                onChange={(e) => setField("price", e.target.value)}
                inputMode="decimal"
              />
            </FormField>
            <FormField label="List price" optional error={errors.listPrice}>
              <Input
                value={values.listPrice ?? ""}
                onChange={(e) => setField("listPrice", e.target.value)}
                inputMode="decimal"
              />
            </FormField>
            <FormField label="Offered price" optional error={errors.offeredPrice}>
              <Input
                value={values.offeredPrice ?? ""}
                onChange={(e) => setField("offeredPrice", e.target.value)}
                inputMode="decimal"
              />
            </FormField>
          </FieldGroup>

          <FieldGroup title="Media">
            <FormField className="sm:col-span-2" label="VDP URL" optional error={errors.vdpUrl}>
              <Input
                type="url"
                value={values.vdpUrl ?? ""}
                onChange={(e) => setField("vdpUrl", e.target.value)}
                placeholder="https://dealer.com/vehicle/123"
              />
            </FormField>
          </FieldGroup>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? "Saving…"
              : mode === "create"
                ? "Add vehicle"
                : "Save vehicle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Select over a canonical vocabulary for an optional free-string field.
 * "" means unset (the "Not set" sentinel); a stored value outside the
 * vocabulary (verbose feed strings) is prepended so the trigger isn't
 * blank and an unrelated edit can't silently overwrite it.
 */
function VocabSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <Select
      value={value || NOT_SET}
      onValueChange={(v) => onChange(v === NOT_SET ? "" : v)}
    >
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NOT_SET}>Not set</SelectItem>
        {value && !options.includes(value) && (
          <SelectItem value={value}>{value}</SelectItem>
        )}
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function FieldGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="grid gap-3">
      <legend className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

async function invalidate(qc: ReturnType<typeof useQueryClient>, dealershipId?: string) {
  if (!dealershipId) return;
  await qc.invalidateQueries({ queryKey: ["admin-bdc", dealershipId, "inventory-search"] });
}
